import { v4 as uuidv4 } from 'uuid';

const USER_ID_KEY = 'analytics_v2_user_id';
const FIRST_VISIT_KEY = 'analytics_v2_first_visit';
const ANALYTICS_AUTH_TOKEN_KEY = 'analytics_auth_token';
const ANALYTICS_AUTH_EXPIRES_AT_KEY = 'analytics_auth_expires_at';

export const isAnalyticsDashboardPath = (pathValue = '') =>
  typeof pathValue === 'string' && pathValue.startsWith('/analytics');

export const hasValidAnalyticsDashboardToken = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  try {
    const token = localStorage.getItem(ANALYTICS_AUTH_TOKEN_KEY);
    const expiresAt = Number(localStorage.getItem(ANALYTICS_AUTH_EXPIRES_AT_KEY));
    return Boolean(token) && Number.isFinite(expiresAt) && expiresAt > Date.now();
  } catch {
    return false;
  }
};

export const shouldSuppressAnalyticsSession = (pathValue = '') =>
  isAnalyticsDashboardPath(pathValue) || hasValidAnalyticsDashboardToken();

class ProfileAnalytics {
  constructor() {
    this.userId = this.getOrCreateUserId();
    this.sessionId = uuidv4();
    this.sessionStart = Date.now();
    this.landingPath = this.getCurrentPath();
    this.attribution = this.collectAttribution();
    this.pageViews = 0;
    this.events = [];
    this.sessionEnded = false;
    this.profile = this.collectProfile();
    this.resolvedProfile = {
      location: null,
      network: null,
      ipHash: null,
    };
    this.trackingSuppressed = shouldSuppressAnalyticsSession(this.landingPath);

    this.handleSessionClose = this.handleSessionClose.bind(this);
    this.start();
  }

  start() {
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.handleSessionClose);
      window.addEventListener('beforeunload', this.handleSessionClose);
    }

    if (this.syncTrackingSuppression(this.landingPath)) {
      return;
    }

    this.sendEvent('session_start', {
      path: this.landingPath,
      page: this.landingPath,
    });
  }

  handleSessionClose() {
    this.endSession(true);
  }

  getCurrentPath() {
    if (typeof window === 'undefined') return '/';
    return window.location?.pathname || '/';
  }

  getCurrentLocation() {
    if (typeof window === 'undefined') return null;
    return window.location || null;
  }

  syncTrackingSuppression(pathValue = this.getCurrentPath()) {
    if (this.trackingSuppressed) {
      return true;
    }

    const nextSuppressed = shouldSuppressAnalyticsSession(pathValue);
    if (nextSuppressed) {
      this.trackingSuppressed = true;
    }

    return this.trackingSuppressed;
  }

  getReferrer() {
    if (typeof document === 'undefined') return 'direct';
    return document.referrer || 'direct';
  }

  getReferrerHost() {
    const referrer = this.getReferrer();
    if (!referrer || referrer === 'direct') {
      return 'direct';
    }

    try {
      return new URL(referrer).host || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  getDestinationHost(href) {
    if (typeof href !== 'string' || !href) {
      return '';
    }

    if (href.startsWith('mailto:')) {
      return 'mailto';
    }

    if (href.startsWith('tel:')) {
      return 'tel';
    }

    try {
      const base = this.getCurrentLocation()?.origin || 'https://example.com';
      return new URL(href, base).host || '';
    } catch {
      return '';
    }
  }

  collectAttribution() {
    if (typeof window === 'undefined') {
      return {
        landingPath: '/',
        referrer: 'direct',
        referrerHost: 'direct',
        utmSource: '',
        utmMedium: '',
        utmCampaign: '',
        utmTerm: '',
        utmContent: '',
      };
    }

    const searchParams = new URLSearchParams(window.location?.search || '');

    return {
      landingPath: this.landingPath || this.getCurrentPath(),
      referrer: this.getReferrer(),
      referrerHost: this.getReferrerHost(),
      utmSource: searchParams.get('utm_source') || '',
      utmMedium: searchParams.get('utm_medium') || '',
      utmCampaign: searchParams.get('utm_campaign') || '',
      utmTerm: searchParams.get('utm_term') || '',
      utmContent: searchParams.get('utm_content') || '',
    };
  }

  getOrCreateUserId() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return uuidv4();
    }

    try {
      let userId = localStorage.getItem(USER_ID_KEY);
      if (!userId) {
        userId = uuidv4();
        localStorage.setItem(USER_ID_KEY, userId);
        localStorage.setItem(FIRST_VISIT_KEY, String(Date.now()));
      }
      return userId;
    } catch {
      return uuidv4();
    }
  }

  collectProfile() {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return {};
    }

    const browser = this.getBrowserInfo();
    const os = this.getOSInfo();
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    return {
      deviceType: this.getDeviceType(),
      browserName: browser.name,
      browserVersion: browser.version,
      osName: os.name,
      osVersion: os.version,
      platform: navigator.platform || '',
      language: navigator.language || '',
      languages: Array.isArray(navigator.languages) ? navigator.languages : [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      viewportWidth: window.innerWidth || null,
      viewportHeight: window.innerHeight || null,
      screenWidth: window.screen?.width || null,
      screenHeight: window.screen?.height || null,
      devicePixelRatio: window.devicePixelRatio || null,
      networkEffectiveType: connection?.effectiveType || '',
      networkDownlink: connection?.downlink ?? null,
      networkRtt: connection?.rtt ?? null,
      networkSaveData: Boolean(connection?.saveData),
    };
  }

  getDeviceType() {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  getBrowserInfo() {
    if (typeof navigator === 'undefined') return { name: 'unknown', version: 'unknown' };
    const ua = navigator.userAgent;

    if (ua.includes('Edg')) {
      return { name: 'Edge', version: ua.match(/Edg\/(\d+)/)?.[1] || 'unknown' };
    }
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      return { name: 'Chrome', version: ua.match(/Chrome\/(\d+)/)?.[1] || 'unknown' };
    }
    if (ua.includes('Safari') && !ua.includes('Chrome')) {
      return { name: 'Safari', version: ua.match(/Version\/(\d+)/)?.[1] || 'unknown' };
    }
    if (ua.includes('Firefox')) {
      return { name: 'Firefox', version: ua.match(/Firefox\/(\d+)/)?.[1] || 'unknown' };
    }

    return { name: 'unknown', version: 'unknown' };
  }

  getOSInfo() {
    if (typeof navigator === 'undefined') return { name: 'unknown', version: 'unknown' };
    const ua = navigator.userAgent;

    if (ua.includes('Windows')) {
      return { name: 'Windows', version: ua.match(/Windows NT ([\d.]+)/)?.[1] || 'unknown' };
    }
    if (ua.includes('Mac OS X')) {
      return {
        name: 'macOS',
        version: ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || 'unknown',
      };
    }
    if (ua.includes('Android')) {
      return { name: 'Android', version: ua.match(/Android ([\d.]+)/)?.[1] || 'unknown' };
    }
    if (ua.includes('iPhone') || ua.includes('iPad')) {
      return { name: 'iOS', version: ua.match(/OS ([\d_]+)/)?.[1]?.replace('_', '.') || 'unknown' };
    }
    if (ua.includes('Linux')) {
      return { name: 'Linux', version: 'unknown' };
    }

    return { name: 'unknown', version: 'unknown' };
  }

  trackPageView(path) {
    const normalizedPath = typeof path === 'string' && path.length > 0 ? path : this.getCurrentPath();
    if (this.syncTrackingSuppression(normalizedPath)) {
      return;
    }

    this.pageViews += 1;

    this.sendEvent('page_view', {
      path: normalizedPath,
      page: normalizedPath,
    });
  }

  trackUserAction(action, details = {}) {
    if (!action) {
      return;
    }

    this.sendEvent('user_action', {
      action,
      ...details,
    });
  }

  trackEvent(eventName, data = {}) {
    const candidatePath = data.page || data.path || this.getCurrentPath();
    if (this.syncTrackingSuppression(candidatePath)) {
      return;
    }

    if (eventName === 'page_view') {
      this.trackPageView(candidatePath);
      return;
    }

    if (eventName === 'session_end') {
      this.endSession(Boolean(data.useBeacon));
      return;
    }

    this.sendEvent(eventName, data);
  }

  async sendEvent(eventType, details = {}, useBeacon = false) {
    const ts = Date.now();
    const attribution = this.attribution || this.collectAttribution();
    const path = details.path || details.page || this.getCurrentPath();
    if (this.syncTrackingSuppression(path)) {
      return;
    }

    const eventDetails = {
      href: details.href || '',
      destinationHost: details.destinationHost || this.getDestinationHost(details.href),
      label: details.label || '',
      surface: details.surface || '',
      resumeVersion: details.resumeVersion || '',
      platform: details.platform || '',
      action: details.action || '',
      method: details.method || '',
      paperSlug: details.paperSlug || '',
      paperTitle: details.paperTitle || '',
      assetType: details.assetType || '',
    };
    const payload = {
      schemaVersion: 2,
      eventType,
      eventId: uuidv4(),
      ts,
      userId: this.userId,
      sessionId: this.sessionId,
      path,
      page: details.page || path,
      referrer: details.referrer || attribution.referrer,
      durationMs: details.durationMs || null,
      attribution: {
        landingPath: attribution.landingPath,
        referrerHost: details.referrerHost || attribution.referrerHost,
        utmSource: attribution.utmSource,
        utmMedium: attribution.utmMedium,
        utmCampaign: attribution.utmCampaign,
        utmTerm: attribution.utmTerm,
        utmContent: attribution.utmContent,
      },
      details: eventDetails,
      profile: this.profile,
    };

    this.events.push({
      eventType,
      ts,
      path: payload.path,
      durationMs: payload.durationMs,
    });
    this.events = this.events.slice(-20);

    if (typeof navigator !== 'undefined' && useBeacon && typeof navigator.sendBeacon === 'function') {
      try {
        const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const queued = navigator.sendBeacon('/api/analytics', body);
        if (queued) return;
      } catch {
        // Fall through to fetch.
      }
    }

    if (typeof fetch !== 'function') {
      return;
    }

    try {
      const response = await fetch('/api/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: eventType === 'session_end',
      });

      if (response.ok) {
        const body = await response.json().catch(() => null);
        if (body?.resolvedProfile) {
          this.resolvedProfile = {
            location: body.resolvedProfile.location || this.resolvedProfile.location,
            network: body.resolvedProfile.network || this.resolvedProfile.network,
            ipHash: body.resolvedProfile.ipHash || this.resolvedProfile.ipHash,
          };
        }
      }
    } catch {
      // Fire-and-forget analytics should never interrupt user flows.
    }
  }

  endSession(useBeacon = false) {
    if (this.sessionEnded) {
      return;
    }

    const currentPath = this.getCurrentPath();
    if (this.syncTrackingSuppression(currentPath)) {
      this.sessionEnded = true;
      return;
    }

    this.sessionEnded = true;
    const durationMs = Date.now() - this.sessionStart;

    this.sendEvent(
      'session_end',
      {
        durationMs,
        path: currentPath,
        page: currentPath,
      },
      useBeacon
    );
  }

  getUserProfile() {
    let firstVisit = null;
    try {
      firstVisit = localStorage.getItem(FIRST_VISIT_KEY);
    } catch {
      firstVisit = null;
    }

    return {
      userId: this.userId,
      sessionId: this.sessionId,
      firstVisit,
      landingPath: this.landingPath,
      attribution: this.attribution,
      sessionDuration: Date.now() - this.sessionStart,
      pageViews: this.pageViews,
      profile: this.profile,
      resolvedProfile: this.resolvedProfile,
      trackingSuppressed: this.trackingSuppressed,
    };
  }

  getDetailedStats() {
    const userProfile = this.getUserProfile();

    return {
      userProfile,
      attribution: this.attribution,
      sessionStats: {
        duration: userProfile.sessionDuration,
        pageViews: this.pageViews,
        eventsCount: this.events.length,
      },
      recentEvents: [...this.events],
    };
  }
}

const analytics = new ProfileAnalytics();

export default analytics;

export const trackPageView = (page) => analytics.trackPageView(page);
export const trackUserAction = (action, details) => analytics.trackUserAction(action, details);
export const trackEvent = (event, data) => analytics.trackEvent(event, data);
export const getUserProfile = () => analytics.getUserProfile();
export const getDetailedStats = () => analytics.getDetailedStats();
